/*
 * Copyright (C) 2024 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { ImageCroppedEvent, ImageCropperComponent, ImageTransform } from 'ngx-image-cropper';
import { AnimatedGifHelper } from '../../helpers/animated-gif-helper';
import { FileUploadHelper } from '../../helpers/file-upload-helper';
import { ImageCropperData } from '../../models/images/image-crop.model';

@Component({
  selector: 'ptl-image-crop-dialog',
  templateUrl: './advanced-image-crop-dialog.component.html',
  styleUrls: ['./advanced-image-crop-dialog.component.scss'],
})
export class AdvancedImageCropDialogComponent implements OnInit {
  imageFile?: File;
  imageBase64?: string;
  maintainAspectRatio = false;
  containWithinAspectRatio = false;
  aspectRatio = 1;
  roundCropper = false;
  resizeToWidth = 0;
  resizeToHeight = 0;
  backgroundColor: string | undefined = 'white';
  imageFormat: 'jpeg' | 'png' | 'gif' = 'jpeg';
  description: string;
  transform: ImageTransform = {
    translateUnit: 'px',
  };
  isImageLoading = false;
  isAnimatedGif = false;

  @ViewChild('imageCropper', { static: false }) imageCropper: ImageCropperComponent;

  private croppedImageDataUrl?: string;
  private croppedImageFile?: File;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ImageCropperData,
    private dialogRef: MatDialogRef<AdvancedImageCropDialogComponent>,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.croppedImageFile = this.data.imageFile;
    this.setInputImage(this.data.imageFile);
    this.isImageLoading = true;
    this.maintainAspectRatio = this.data.cropperShape === 'square' || this.data.cropperShape === 'circle' || !!this.data.aspectRatio;
    this.containWithinAspectRatio = this.data.containWithinAspectRatio ?? this.maintainAspectRatio;
    this.roundCropper = this.data.cropperShape === 'circle';
    this.resizeToWidth = this.data.resizeToWidth ? this.data.resizeToWidth : 0;
    this.resizeToHeight = this.data.resizeToHeight ? this.data.resizeToHeight : 0;

    if (this.data.imageFormat === 'png') {
      this.imageFormat = 'png';
      this.backgroundColor = undefined;
    }

    if (this.data.imageFile?.type.includes('gif')) {
      this.imageFormat = 'gif';
    }

    if (this.data.aspectRatio && this.data.cropperShape === 'rectangle') {
      this.aspectRatio = this.data.aspectRatio;
    }
    this.description = this.data.description;
  }

  updateCroppedImage(event: ImageCroppedEvent): void {
    const base64 = event.base64;

    if (this.imageFormat.includes('gif')) {
      const file = this.data.imageFile;
      AnimatedGifHelper.isGifAnimated(file).then((isAnimated) => {
        this.isAnimatedGif = isAnimated;
        AnimatedGifHelper.readFileAsDataURL(file);
        setTimeout(() => {
          this.imageBase64 = AnimatedGifHelper.imageBase64;
          this.croppedImageDataUrl = this.imageBase64;
          this.cdr.detectChanges();
        }, 100);
      });
    } else {
      // Handle other formats (jpeg, png)
      if (base64) {
        this.croppedImageDataUrl = base64;
      } else if (event.blob) {
        const reader = new FileReader();
        reader.readAsDataURL(event.blob);
        reader.onloadend = () => {
          this.croppedImageDataUrl = reader.result as string;
        };
      }
    }
  }

  saveCrop(): void {
    if (this.croppedImageDataUrl) {
      const croppedImage = FileUploadHelper.convertDataUrlToFile(
        this.croppedImageDataUrl,
        this.croppedImageFile.name.toLowerCase(),
        'image/' + this.imageFormat,
      );
      if (croppedImage) {
        this.croppedImageFile = croppedImage;
      }
    }

    this.dialogRef.close({
      croppedImageFile: this.croppedImageFile,
      croppedImageDataUrl: this.croppedImageDataUrl,
    });
  }

  cancelCrop(): void {
    this.dialogRef.close();
  }

  rotateLeft(): void {
    this.transform = {
      ...this.transform,
      rotate: (this.transform.rotate || 0) - 90,
    };
  }

  zoomIn(): void {
    this.transform = {
      ...this.transform,
      scale: (this.transform.scale || 1) + 0.1,
    };
  }

  zoomOut(): void {
    this.transform = {
      ...this.transform,
      scale: (this.transform.scale || 1) - 0.1,
    };
  }

  flipHorizontal(): void {
    this.transform = {
      ...this.transform,
      flipH: !this.transform.flipH,
    };
  }

  flipVertical(): void {
    this.transform = {
      ...this.transform,
      flipV: !this.transform.flipV,
    };
  }

  resetCropper(): void {
    this.transform = {
      rotate: 0,
      scale: 1,
      flipH: false,
      flipV: false,
    };
    this.imageCropper.resetCropperPosition();
  }

  imageLoaded(): void {
    this.isImageLoading = false;
  }

  private setInputImage(imageFile: File): void {
    if (imageFile?.type.includes('svg')) {
      const canvasEl = document.createElement('canvas') as HTMLCanvasElement;
      const context = canvasEl.getContext('2d');
      const image = new Image();
      const reader = new FileReader();

      reader.onload = (event) => {
        image.onload = () => {
          canvasEl.width = image.naturalWidth;
          canvasEl.height = image.naturalHeight;
          if (context) {
            context?.drawImage(image, 0, 0);
          }
          this.croppedImageDataUrl = this.imageBase64 = canvasEl.toDataURL('image/png');
        };

        if (event.target) {
          image.src = event.target.result as string;
        }
      };

      reader.readAsDataURL(imageFile);
    } else {
      this.imageFile = imageFile;
    }
  }
}
